import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import api, { API } from '@/lib/api';
import { tokenStorage } from '@/lib/tokenStorage';
import { useAuth } from '@/lib/auth';
import { TECH_SYSTEMS, systemLabel } from '@/lib/systems';
import { Plus, Trash2, X, Download, Upload as UploadIcon, Edit3, BookOpen, CheckCircle2, AlertTriangle, Clock3, ChevronRight, ChevronDown, Wrench } from 'lucide-react';

// Normalize brand for grouping: case + accents + formatting tolerant.
function _normBrand(b) {
  return (b || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

const RESULTS = [
  { value: 'risolto', label: 'Risolto', color: 'bg-green-100 text-green-800', Icon: CheckCircle2 },
  { value: 'parziale', label: 'Parziale', color: 'bg-yellow-100 text-yellow-800', Icon: AlertTriangle },
  { value: 'in_corso', label: 'In corso', color: 'bg-blue-100 text-blue-800', Icon: Clock3 },
  { value: 'non_risolto', label: 'Non risolto', color: 'bg-red-100 text-red-800', Icon: AlertTriangle },
];

const EMPTY = {
  brand: '', model: '', serial_number: '', error_code: '',
  system: 'electronics',
  symptom: '',
  environment: '', root_cause: '', resolution_steps: '', parts_replaced: '',
  time_to_fix_min: 30, result: 'risolto', confidence: 4, learnings: '',
};

export default function FieldReportsPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'it';
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [expandedBrands, setExpandedBrands] = useState({});
  const [expandedModels, setExpandedModels] = useState({});
  const [search, setSearch] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Group reports by brand > model
  const grouped = useMemo(() => {
    const filtered = items.filter((r) => {
      if (systemFilter && r.system !== systemFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return [r.brand, r.model, r.error_code, r.symptom, r.root_cause, r.learnings]
          .some((f) => (f || '').toLowerCase().includes(q));
      }
      return true;
    });
    const tree = {};
    for (const r of filtered) {
      const bRaw = r.brand || '—';
      const bKey = _normBrand(bRaw) || '__';
      const m = r.model || '—';
      tree[bKey] = tree[bKey] || { display: bRaw, variants: [], models: {} };
      tree[bKey].variants.push(bRaw);
      tree[bKey].models[m] = tree[bKey].models[m] || [];
      tree[bKey].models[m].push(r);
    }
    return Object.entries(tree).sort((a, b) => a[1].display.localeCompare(b[1].display, undefined, { sensitivity: 'base' })).map(([key, info]) => {
      // Pick canonical display (first non-empty mixed-case variant, else most common)
      const counts = {};
      for (const v of info.variants) counts[v] = (counts[v] || 0) + 1;
      const display = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      return {
        brand: display,
        models: Object.keys(info.models).sort().map((model) => ({
          model,
          reports: info.models[model],
        })),
        totalReports: Object.values(info.models).reduce((s, arr) => s + arr.length, 0),
      };
    });
  }, [items, search, systemFilter]);

  const toggleBrand = (b) => setExpandedBrands((p) => ({ ...p, [b]: !p[b] }));
  const toggleModel = (key) => setExpandedModels((p) => ({ ...p, [key]: !p[key] }));

  // Auto-expand first brand when search is active
  useEffect(() => {
    if (search.trim() && grouped.length > 0) {
      const allBrands = {};
      const allModels = {};
      grouped.forEach((g) => {
        allBrands[g.brand] = true;
        g.models.forEach((m) => { allModels[`${g.brand}::${m.model}`] = true; });
      });
      setExpandedBrands(allBrands);
      setExpandedModels(allModels);
    }
  }, [search, grouped]);

  const load = useCallback(() => api.get('/field-reports').then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ ...EMPTY, ...r });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/field-reports/${editing.id}`, { ...form, time_to_fix_min: Number(form.time_to_fix_min), confidence: Number(form.confidence) });
        toast.success('Report aggiornato e reindicizzato per l\'AI');
      } else {
        await api.post('/field-reports', { ...form, time_to_fix_min: Number(form.time_to_fix_min), confidence: Number(form.confidence) });
        toast.success('Report creato e aggiunto alla base di apprendimento');
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore'); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/field-reports/${confirmDelete.id}`);
      toast.success('Report eliminato e rimosso dalla base di apprendimento');
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    } finally {
      setDeleting(false);
    }
  };

  const downloadCsv = async () => {
    const token = tokenStorage.get();
    const res = await fetch(`${API}/field-reports/export/csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `field_reports_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('CSV scaricato');
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    try {
      const { data } = await api.post('/field-reports/import/csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const total = (data.created || 0) + (data.updated || 0);
      if (total === 0) {
        const errs = (data.errors || []).slice(0, 3).join('\n');
        toast.error(`Nessun report importato. Separatore rilevato: "${data.delimiter}". Errori:\n${errs || 'formato non valido'}`);
      } else {
        toast.success(`Import OK — ${data.created} creati, ${data.updated} aggiornati (sep: "${data.delimiter}")${data.errors?.length ? `, ${data.errors.length} righe saltate` : ''}`);
        if (data.errors?.length) {
          console.warn('CSV import warnings:', data.errors);
        }
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore import: verifica formato file');
    }
    e.target.value = '';
  };

  const downloadTemplate = () => {
    const headers = 'brand,model,serial_number,error_code,symptom,environment,root_cause,resolution_steps,parts_replaced,time_to_fix_min,result,confidence,learnings';
    const example = 'Dulevo,D.Zero2,SN-001,E42,Motore trazione non parte,35C alta umidita,Sensore Hall danneggiato,"1. Scollegare 2. Sostituire sensore",Sensore Hall P/N 12345,45,risolto,5,Con temp >30C controllare sensore Hall';
    const blob = new Blob([headers + '\n' + example], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'field_reports_template.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const getResult = (r) => RESULTS.find((x) => x.value === r) || RESULTS[0];

  return (
    <div className="animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-[#002FA7]">FIELD REPORTS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-2">Base di apprendimento</h1>
          <p className="text-gray-500 mt-2 max-w-2xl">Ogni report diventa una regola diagnostica consultabile dall'AI. Usa il CSV per aggiornamenti bulk offline e riportarlo su in modo continuo.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-secondary" data-testid="template-csv-btn" title="Scarica template CSV vuoto per compilare">
            <Download className="w-4 h-4" /> Template
          </button>
          <button onClick={downloadCsv} className="btn-secondary" data-testid="export-csv-btn">
            <Download className="w-4 h-4" /> Export CSV
          </button>
          {user?.role === 'admin' && (
            <label className="btn-secondary cursor-pointer" data-testid="import-csv-label">
              <UploadIcon className="w-4 h-4" /> Import CSV
              <input type="file" accept=".csv" onChange={uploadCsv} className="hidden" data-testid="import-csv-input" />
            </label>
          )}
          <button onClick={startNew} className="btn-primary" data-testid="new-report-btn">
            <Plus className="w-4 h-4" /> Nuovo report
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <input
          type="text"
          placeholder="Cerca per marca, modello, codice, sintomo o lezione…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input-base sm:col-span-2"
          data-testid="search-reports"
        />
        <select
          value={systemFilter}
          onChange={(e) => setSystemFilter(e.target.value)}
          className="input-base"
          data-testid="fr-system-filter"
        >
          <option value="">{t('app.all')}</option>
          {TECH_SYSTEMS.map((s) => (<option key={s} value={s}>{systemLabel(s, lang)}</option>))}
        </select>
      </div>

      <div className="mt-6 space-y-3" data-testid="reports-list">
        {items.length === 0 ? (
          <div className="surface p-12 text-center">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto" strokeWidth={1.2} />
            <div className="mt-3 font-display font-bold text-lg">Nessun report ancora</div>
            <div className="text-sm text-gray-500">Aggiungi il primo report per iniziare ad addestrare l'AI con i casi reali.</div>
          </div>
        ) : grouped.length === 0 ? (
          <div className="surface p-8 text-center text-sm text-gray-500">
            Nessun report trovato per "{search}".
          </div>
        ) : grouped.map((group) => {
          const isBrandOpen = !!expandedBrands[group.brand];
          return (
            <div key={group.brand} className="surface overflow-hidden" data-testid={`brand-${group.brand}`}>
              <button
                onClick={() => toggleBrand(group.brand)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                data-testid={`toggle-brand-${group.brand}`}
              >
                <div className="flex items-center gap-3">
                  {isBrandOpen ? <ChevronDown className="w-5 h-5 text-[#002FA7]" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
                  <div className="w-8 h-8 bg-[#002FA7]/10 text-[#002FA7] flex items-center justify-center">
                    <Wrench className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <div className="font-display font-bold text-lg tracking-tight">{group.brand}</div>
                    <div className="text-xs text-gray-500 font-mono">{group.models.length} modelli · {group.totalReports} report</div>
                  </div>
                </div>
                <span className="overline text-gray-500">{isBrandOpen ? 'Chiudi' : 'Apri'}</span>
              </button>
              {isBrandOpen && (
                <div className="border-t border-gray-200">
                  {group.models.map((mg) => {
                    const mkey = `${group.brand}::${mg.model}`;
                    const isModelOpen = !!expandedModels[mkey];
                    return (
                      <div key={mkey} className="border-b border-gray-100 last:border-b-0" data-testid={`model-${mg.model}`}>
                        <button
                          onClick={() => toggleModel(mkey)}
                          className="w-full flex items-center justify-between pl-12 pr-4 py-3 hover:bg-gray-50 transition-colors text-left"
                          data-testid={`toggle-model-${mg.model}`}
                        >
                          <div className="flex items-center gap-3">
                            {isModelOpen ? <ChevronDown className="w-4 h-4 text-[#002FA7]" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            <div className="font-semibold">{mg.model}</div>
                            <span className="font-mono text-xs text-gray-500">({mg.reports.length})</span>
                          </div>
                        </button>
                        {isModelOpen && (
                          <div className="bg-gray-50/50 px-4 pb-3 space-y-2">
                            {mg.reports.map((r) => {
                              const res = getResult(r.result);
                              return (
                                <div key={r.id} className="bg-white border border-gray-200 p-4" data-testid={`report-${r.id}`}>
                                  <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-xs text-[#002FA7] font-bold">#{r.id.slice(0, 8)}</span>
                                        {r.system && (
                                          <span className="overline text-[#002FA7] px-2 py-0.5 bg-[#002FA7]/10 uppercase">{systemLabel(r.system, lang)}</span>
                                        )}
                                        {r.error_code && <span className="bg-red-100 text-red-800 px-2 py-0.5 text-xs font-mono font-bold">{r.error_code}</span>}
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold ${res.color}`}>
                                          <res.Icon className="w-3 h-3" /> {res.label}
                                        </span>
                                        <span className="text-xs text-gray-500 font-mono">conf {r.confidence}/5</span>
                                      </div>
                                      <div className="text-xs text-gray-500 mt-1 font-mono">
                                        {r.technician_name} · {new Date(r.updated_at).toLocaleString('it-IT')} · {r.time_to_fix_min} min
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <button onClick={() => startEdit(r)} className="btn-ghost text-xs" data-testid={`edit-${r.id}`}>
                                        <Edit3 className="w-3.5 h-3.5" /> Modifica
                                      </button>
                                      {(user?.role === 'admin' || user?.id === r.created_by) && (
                                        <button
                                          onClick={() => setConfirmDelete(r)}
                                          className="inline-flex items-center gap-1.5 bg-red-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-red-700 active:scale-95 transition-all"
                                          data-testid={`del-${r.id}`}
                                        >
                                          <Trash2 className="w-3.5 h-3.5" /> Elimina
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-sm">
                                    <div>
                                      <div className="overline text-gray-500 mb-1">Sintomo</div>
                                      <div className="text-gray-900">{r.symptom}</div>
                                    </div>
                                    <div>
                                      <div className="overline text-gray-500 mb-1">Causa</div>
                                      <div className="text-gray-900">{r.root_cause}</div>
                                    </div>
                                    <div>
                                      <div className="overline text-gray-500 mb-1">Procedura</div>
                                      <div className="text-gray-900 whitespace-pre-wrap">{r.resolution_steps}</div>
                                    </div>
                                  </div>
                                  {(r.parts_replaced || r.environment || r.learnings) && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-gray-100 text-sm">
                                      {r.parts_replaced && <div><div className="overline text-gray-500 mb-1">Ricambi</div><div className="font-mono text-xs">{r.parts_replaced}</div></div>}
                                      {r.environment && <div><div className="overline text-gray-500 mb-1">Condizioni</div><div className="text-gray-700">{r.environment}</div></div>}
                                      {r.learnings && <div><div className="overline text-yellow-700 mb-1">🎓 Lezione appresa</div><div className="text-gray-700">{r.learnings}</div></div>}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="bg-white w-full sm:max-w-3xl border border-gray-200 max-h-[95vh] overflow-y-auto" data-testid="report-form">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="font-display font-bold text-lg">{editing ? 'Modifica field report' : 'Nuovo field report'}</div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><label className="overline text-gray-600 block mb-1">Marca *</label>
                  <input required className="input-base" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="fr-brand" /></div>
                <div><label className="overline text-gray-600 block mb-1">Modello *</label>
                  <input required className="input-base" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} data-testid="fr-model" /></div>
                <div><label className="overline text-gray-600 block mb-1">N. serie</label>
                  <input className="input-base font-mono" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
                <div><label className="overline text-gray-600 block mb-1">Codice errore</label>
                  <input className="input-base font-mono" value={form.error_code} onChange={(e) => setForm({ ...form, error_code: e.target.value })} data-testid="fr-code" /></div>
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">{t('patterns.system')} *</label>
                <select required className="input-base" value={form.system}
                  onChange={(e) => setForm({ ...form, system: e.target.value })}
                  data-testid="fr-system">
                  {TECH_SYSTEMS.map((s) => (
                    <option key={s} value={s}>{systemLabel(s, lang)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">Sintomo osservato *</label>
                <textarea required rows={2} className="input-base" value={form.symptom} onChange={(e) => setForm({ ...form, symptom: e.target.value })} data-testid="fr-symptom" />
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">Causa individuata (root cause) *</label>
                <textarea required rows={2} className="input-base" value={form.root_cause} onChange={(e) => setForm({ ...form, root_cause: e.target.value })} data-testid="fr-cause" />
              </div>
              <div>
                <label className="overline text-gray-600 block mb-1">Procedura risolutiva (step-by-step) *</label>
                <textarea required rows={4} className="input-base font-mono text-xs" placeholder="1. ...&#10;2. ...&#10;3. ..." value={form.resolution_steps} onChange={(e) => setForm({ ...form, resolution_steps: e.target.value })} data-testid="fr-steps" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><label className="overline text-gray-600 block mb-1">Ricambi sostituiti</label>
                  <input className="input-base font-mono" value={form.parts_replaced} onChange={(e) => setForm({ ...form, parts_replaced: e.target.value })} /></div>
                <div><label className="overline text-gray-600 block mb-1">Condizioni ambientali</label>
                  <input className="input-base" placeholder="es. 35°C, alta umidità" value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="overline text-gray-600 block mb-1">Esito</label>
                  <select className="input-base" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}>
                    {RESULTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select></div>
                <div><label className="overline text-gray-600 block mb-1">Tempo (min)</label>
                  <input type="number" min="0" className="input-base font-mono" value={form.time_to_fix_min} onChange={(e) => setForm({ ...form, time_to_fix_min: e.target.value })} /></div>
                <div><label className="overline text-gray-600 block mb-1">Confidenza 1-5</label>
                  <input type="number" min="1" max="5" className="input-base font-mono" value={form.confidence} onChange={(e) => setForm({ ...form, confidence: e.target.value })} /></div>
              </div>
              <div>
                <label className="overline text-yellow-700 block mb-1">🎓 Lezione appresa (per future diagnosi)</label>
                <textarea rows={2} className="input-base" placeholder="es. 'Quando E42 compare con temp >30°C, controllare anche il sensore Hall prima del cavo X4'" value={form.learnings} onChange={(e) => setForm({ ...form, learnings: e.target.value })} />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="btn-secondary">Annulla</button>
              <button type="submit" className="btn-primary" data-testid="fr-submit">
                {editing ? 'Salva modifiche' : 'Crea e indicizza'}
              </button>
            </div>
          </form>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-fade-up" onClick={() => !deleting && setConfirmDelete(null)} data-testid="confirm-delete-modal">
          <div className="bg-white border border-gray-200 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-200 flex items-start gap-3">
              <div className="w-10 h-10 bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-lg">Eliminare il field report?</div>
                <div className="text-sm text-gray-600 mt-1 font-mono truncate">
                  #{confirmDelete.id.slice(0, 8)} — {confirmDelete.brand} {confirmDelete.model}
                  {confirmDelete.error_code ? ` · ${confirmDelete.error_code}` : ''}
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Verrà rimosso anche dalla <b>base di apprendimento AI</b> (il chunk indicizzato viene eliminato). L'azione è irreversibile.
                </div>
              </div>
              <button onClick={() => !deleting && setConfirmDelete(null)} className="btn-ghost shrink-0"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} disabled={deleting} className="btn-secondary" data-testid="fr-confirm-cancel">
                Annulla
              </button>
              <button onClick={remove} disabled={deleting} className="inline-flex items-center gap-2 bg-red-600 text-white px-5 py-2.5 font-semibold hover:bg-red-700 active:scale-95 transition-all disabled:opacity-50" data-testid="fr-confirm-delete">
                <Trash2 className="w-4 h-4" />
                {deleting ? 'Eliminazione…' : 'Sì, elimina'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
